import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { StorageDestinationListUnit } from 'mbs-ui-shared@models/StorageAccount';

interface StorageDestinationsState {
  entities: Record<string, StorageDestinationListUnit>;
  ids: string[];
  // The office-only subset is computed on the backend, so it is cached as its own id list over the shared entity map
  officeIds: string[];
  loading: boolean;
  loaded: boolean;
  officeLoaded: boolean;
}

const initialState: StorageDestinationsState = {
  entities: {},
  ids: [],
  officeIds: [],
  loading: false,
  loaded: false,
  officeLoaded: false
};

export const StorageDestinationsStore = signalStore(
  { providedIn: 'root' },
  withDevtools('StorageDestinationsStore'),
  withState<StorageDestinationsState>(initialState),
  withComputed((store) => ({
    all: computed(() =>
      store
        .ids()
        .map((id) => store.entities()[id])
        .filter(Boolean)
    ),
    office: computed(() =>
      store
        .officeIds()
        .map((id) => store.entities()[id])
        .filter(Boolean)
    )
  })),
  withMethods((store) => ({
    setLoading(loading: boolean): void {
      patchState(store, { loading });
    },
    setAll(destinations: StorageDestinationListUnit[]): void {
      const entities: Record<string, StorageDestinationListUnit> = {};
      const ids: string[] = [];

      for (const destination of destinations) {
        entities[destination.id] = destination;
        ids.push(destination.id);
      }

      patchState(store, { entities, ids, loaded: true, loading: false });
    },
    setOffice(destinations: StorageDestinationListUnit[]): void {
      const entities: Record<string, StorageDestinationListUnit> = { ...store.entities() };
      const officeIds: string[] = [];

      for (const destination of destinations) {
        entities[destination.id] = destination;
        officeIds.push(destination.id);
      }

      patchState(store, { entities, officeIds, officeLoaded: true });
    },
    invalidate(): void {
      patchState(store, initialState);
    }
  }))
);

export type StorageDestinationsStoreType = InstanceType<typeof StorageDestinationsStore>;
